import type {
  AccountBatchSourceType,
  ExtractionMode,
  PlatformSelection,
  SourcePlatform,
} from '@/entities/models'

export interface PlatformUiCapability {
  platform: SourcePlatform
  label: string
  single: boolean
  batchModes: readonly AccountBatchSourceType[]
}

export type ServerEnabledCapabilities = Readonly<
  Partial<Record<SourcePlatform, readonly ExtractionMode[]>>
>

export type ServerCapabilitiesSource =
  | ServerEnabledCapabilities
  | (() => ServerEnabledCapabilities)

export interface ResolvedPlatformUiCapability extends PlatformUiCapability {
  singleEnabled: boolean
  enabledBatchModes: AccountBatchSourceType[]
}

export const platformUiCapabilities: readonly PlatformUiCapability[] = [
  {
    platform: 'douyin',
    label: '抖音',
    single: true,
    batchModes: ['profile', 'collection'],
  },
  {
    platform: 'bilibili',
    label: 'B站',
    single: true,
    batchModes: ['profile', 'collection', 'season'],
  },
  {
    platform: 'xiaohongshu',
    label: '小红书',
    single: true,
    batchModes: ['profile', 'collection'],
  },
  {
    platform: 'youtube',
    label: 'YouTube',
    single: true,
    batchModes: ['channel', 'playlist'],
  },
  {
    platform: 'wechat_channels',
    label: '视频号',
    single: true,
    batchModes: [],
  },
]

export const conservativeServerCapabilities: ServerEnabledCapabilities = {
  douyin: ['video', 'profile', 'collection'],
}

export function readServerEnabledCapabilities(
  source: ServerCapabilitiesSource = conservativeServerCapabilities,
): ServerEnabledCapabilities {
  return typeof source === 'function' ? source() : source
}

export function normalizeServerEnabledCapabilities(
  value: unknown,
): ServerEnabledCapabilities {
  if (value === undefined || value === null) {
    return { ...conservativeServerCapabilities }
  }
  if (typeof value !== 'object' || Array.isArray(value)) {
    return { ...conservativeServerCapabilities }
  }
  const record = value as Record<string, unknown>
  const normalized: Partial<Record<SourcePlatform, readonly ExtractionMode[]>> = {}
  for (const display of platformUiCapabilities) {
    const candidate = record[display.platform]
    if (candidate === undefined) continue
    if (!Array.isArray(candidate)) continue
    const allowedModes: readonly ExtractionMode[] = [
      ...(display.single ? (['video'] as const) : []),
      ...display.batchModes,
    ]
    if (
      candidate.some(
        (mode) =>
          typeof mode !== 'string' ||
          !allowedModes.some((allowed) => allowed === mode),
      ) ||
      new Set(candidate).size !== candidate.length
    ) {
      continue
    }
    normalized[display.platform] = allowedModes.filter((mode) =>
      candidate.some((candidateMode) => candidateMode === mode),
    )
  }
  return normalized
}

export const capabilityKey = (platform: PlatformSelection, mode: ExtractionMode) =>
  `${platform}:${mode}`

export function isCapabilityEnabled(
  enabled: ServerEnabledCapabilities,
  platform: SourcePlatform,
  mode: ExtractionMode,
) {
  const display = platformUiCapabilities.find((item) => item.platform === platform)
  if (!display) return false
  const displayed = mode === 'video' ? display.single : display.batchModes.includes(mode)
  return displayed && enabled[platform]?.some((value) => value === mode) === true
}

export function isSelectionCapabilityEnabled(
  enabled: ServerEnabledCapabilities,
  platformSelection: PlatformSelection,
  mode: ExtractionMode,
) {
  if (platformSelection !== 'auto') {
    return isCapabilityEnabled(enabled, platformSelection, mode)
  }
  return platformUiCapabilities.some(({ platform }) =>
    isCapabilityEnabled(enabled, platform, mode),
  )
}

export function resolvePlatformUiCapabilities(
  enabled: ServerEnabledCapabilities = conservativeServerCapabilities,
): ResolvedPlatformUiCapability[] {
  return platformUiCapabilities.map((display) => ({
    ...display,
    singleEnabled: display.single && isCapabilityEnabled(enabled, display.platform, 'video'),
    enabledBatchModes: display.batchModes.filter((mode) =>
      isCapabilityEnabled(enabled, display.platform, mode),
    ),
  }))
}
